import { useRef } from 'react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    CreateUserModel,
    UserModel,
} from '@mesterplan/shared-types';
import type { CompanyMemberSort } from '@mesterplan/api-client';
import { useFirebase } from '../context';
import { CACHE_KEYS, NotificationCallbacks, CompanyContext } from '../types';
import {
    isGuid,
    mapCurrentUserDto,
    mapMemberDto,
    mapUserDto,
    toUpdateMePayload,
    toUpdateUserPayload,
} from './userApiBridge';

/** The server's page-size cap — `useMembersByIds` asks for at most this many ids per request. */
export const MEMBERS_BY_IDS_BATCH = 200;

/**
 * Distinct ids, sorted (a stable query key), split into server-sized batches — every id is
 * asked for, none silently dropped past the page cap.
 */
export function batchMemberIds(userIds: readonly string[], batchSize = MEMBERS_BY_IDS_BATCH): string[][] {
    const ids = [...new Set(userIds)].sort();
    const batches: string[][] = [];
    for (let i = 0; i < ids.length; i += batchSize) {
        batches.push(ids.slice(i, i + batchSize));
    }
    return batches;
}

/** Page size for member pickers: small enough to open instantly, large enough to rarely need page 2. */
export const MEMBER_PICKER_PAGE_SIZE = 100;

export interface MemberSearchPage {
    items: UserModel[];
    page: number;
    pageSize: number;
    total: number;
}

export interface UseMemberSearchOptions {
    /** Sent to `GET .../members/search?search=` — matched server-side on name and email. */
    search: string;
    /** Nothing is fetched until this is true — pass the picker's open state. */
    enabled: boolean;
    companyId?: string;
    pageSize?: number;
    /** Left out server-side — people the picker must not offer (already participants, mapped). */
    excludeUserIds?: readonly string[];
}


export interface UseMembersPageOptions {
    page: number;
    pageSize: number;
    /** Matched server-side on name and email. Callers debounce it. */
    search: string;
    /** Only members on one of these teams; empty = all. */
    teamIds?: readonly string[];
    sort?: CompanyMemberSort;
    desc?: boolean;
}

/**
 * Detect changes between old and new user data
 */
export interface CreateUserWithCompanyModel extends CreateUserModel {
    companyId?: string;
    role?: 'user' | 'admin' | 'owner';
}

interface UseUserOperationsOptions {
    notifications?: NotificationCallbacks;
    selectedCompany?: CompanyContext | null;
}

export function useUserOperations(options: UseUserOperationsOptions = {}) {
    const queryClient = useQueryClient();
    const { currentUser, api } = useFirebase();
    const { notifications, selectedCompany } = options;
    // Ids the last invite left pending (account exists, email unconfirmed), so onSuccess can say so.
    const pendingUserIds = useRef<Set<string>>(new Set());

    const useCreateUserWithAuth = () => {
        return useMutation({
            mutationFn: async (data: CreateUserWithCompanyModel): Promise<string> => {
                // .NET/IDP invite path. Authorization is enforced by the backend
                // `addMember` ([RequirePermission(ManageMembers)]); a 403 flows
                // through the error mapping below. No Firebase `currentUser` and no
                // Firestore COMPANIES pre-read — those were the dead path that made
                // this mutation a silent no-op on the live stack.
                if (!data.companyId) {
                    throw new Error('Virksomheden findes ikke.');
                }
                if (!api) {
                    throw new Error('Fejl ved oprettelse af bruger');
                }

                const result = await api.companies.addMember(data.companyId, {
                    email: data.email,
                    displayName: data.name,
                    role: data.role ?? 'user',
                });

                if (!result.ok) {
                    // The dialog surfaces only the thrown Error.message (no
                    // notifications are passed), so throw FINISHED Danish strings.
                    if (result.error.status === 409) {
                        throw new Error('En bruger med denne email er allerede medlem af virksomheden.');
                    }
                    if (result.error.status === 400) {
                        throw new Error(result.error.detail ?? result.error.title ?? 'Ugyldige oplysninger.');
                    }
                    throw new Error(result.error.detail ?? result.error.title ?? 'Fejl ved oprettelse af bruger');
                }

                const { member, userCreated } = result.data;
                const userId = member.userId;
                if (result.data.pending === true) {
                    pendingUserIds.current.add(userId);
                }

                // Apply the extension profile fields (salary/address/skills/…) ONLY
                // for a freshly created user. For an EXISTING user (userCreated=false)
                // an unconditional update would clobber their real .NET profile with
                // whatever was typed into the create form — there is no Firestore
                // shadow to fall back to on web. Best-effort: a failure here must NOT
                // fail the invite, which already succeeded.
                if (userCreated) {
                    try {
                        const ext = await api.users.update(userId, toUpdateUserPayload(data));
                        if (!ext.ok) {
                            console.error('Error applying extension fields after invite:', ext.error);
                        }
                    } catch (extError) {
                        console.error('Error applying extension fields after invite:', extError);
                    }
                }

                return userId;
            },
            onSuccess: (userId, variables) => {
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.USERS] });
                if (userId) {
                    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.USER, userId] });
                }
                if (variables.companyId) {
                    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.COMPANY, variables.companyId] });
                }
                if (userId && pendingUserIds.current.delete(userId)) {
                    // The account already existed with an unconfirmed email: no access yet.
                    notifications?.onSuccess?.(
                        `${variables.name} har allerede en konto, men emailen er ikke bekræftet. ` +
                        'Personen får adgang, når emailen er bekræftet, og står som "Afventer bekræftelse af email" under Brugere.'
                    );
                    return;
                }
                notifications?.onSuccess?.(
                    `Bruger oprettet! ${variables.name} vil modtage en email med login-instruktioner.`
                );
            },
        });
    };

    const useFetchUser = (userId?: string) => {
        const targetUserId = userId || currentUser?.uid;
        const isSelf = !!targetUserId && targetUserId === currentUser?.uid;

        return useQuery({
            queryKey: [CACHE_KEYS.USER, targetUserId],
            queryFn: async (): Promise<UserModel | undefined> => {
                if (!targetUserId) return undefined;

                try {
                    // The Firestore USERS shadow that used to seed createdAt/updatedAt is gone.
                    // It was the FIRST await here and unconditional, and `firestore.rules` gates
                    // USERS reads on `request.auth != null` — which the IDP path never has — so it
                    // threw permission-denied and the catch below rethrew before either .NET call
                    // could run. The mappers already treat the shadow as optional.

                    // Self read → .NET `me()`, keyed on identity (the Firebase uid is a
                    // push-id, so isGuid would wrongly route to Firestore).
                    if (api && isSelf) {
                        const meResult = await api.users.me();
                        if (meResult.ok) {
                            return mapCurrentUserDto(targetUserId, meResult.data);
                        }
                    }

                    // Admin by-id read → .NET `get(id)` when the id is a Guid
                    // (member ids from companies.listMembers are RsConnect subs).
                    if (api && !isSelf && isGuid(targetUserId)) {
                        const getResult = await api.users.get(targetUserId);
                        if (getResult.ok) {
                            return mapUserDto(getResult.data);
                        }
                        if (getResult.error?.status && getResult.error.status !== 404 && getResult.error.status !== 403) {
                            console.error('Error fetching user from .NET API:', getResult.error);
                        }
                    }

                    notifications?.onError?.('Bruger ikke fundet');
                    return undefined;
                } catch (error) {
                    console.error('Error fetching user:', error);
                    notifications?.onError?.('Fejl ved hentning af bruger');
                    throw error;
                }
            },
            enabled: !!targetUserId,
        });
    };

    const useUpdateUser = () => {
        return useMutation({
            mutationFn: async ({ userId, data }: { userId: string; data: Partial<CreateUserModel> }) => {
                try {
                    // Admin by-id write → .NET `update(id)`. The old Firestore branch began with
                    // an unconditional `getDoc(USERS/{id})` to seed the change log; USERS reads
                    // require a Firebase session the IDP path does not have, so it threw
                    // permission-denied and this catch rethrew — `api.users.update` was never
                    // reached and editing an employee always failed. .NET UpdateUserHandler
                    // records the change-log entry atomically, so nothing is lost.
                    {
                        const result = await api?.users.update(userId, toUpdateUserPayload(data));
                        if (!result) {
                            notifications?.onError?.('Fejl ved opdatering af bruger');
                            throw new Error('Brugeropdatering kræver .NET-klienten');
                        }
                        if (!result.ok) {
                            notifications?.onError?.('Fejl ved opdatering af bruger');
                            throw new Error(result.error?.detail ?? result.error?.title ?? 'Bruger kunne ikke opdateres');
                        }
                    }
                } catch (error) {
                    console.error('Error updating user:', error);
                    notifications?.onError?.('Fejl ved opdatering af bruger');
                    throw error;
                }
            },
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.USERS] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.USER, variables.userId] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.ACTIVITY_LOG] });
                notifications?.onSuccess?.('Bruger opdateret');
            },
        });
    };

    const useUpdateCurrentUser = () => {
        return useMutation({
            mutationFn: async (data: Partial<CreateUserModel>) => {
                // Self write → .NET `updateMe` (display name + phone). Email is
                // RsConnect-managed and rejected by the backend, so it is never sent.
                if (api) {
                    const result = await api.users.updateMe(toUpdateMePayload(data));
                    if (result.ok) {
                        return;
                    }
                    // API is the source of truth when mounted: a real error (e.g. 500)
                    // must propagate to `onError`, NOT fall through to a Firestore write
                    // that would re-show the stale value on the next self-read.
                    throw new Error(result.error?.detail ?? result.error?.title ?? 'Profil kunne ikke opdateres');
                }

                // No Firestore fallback: USERS writes required a Firebase session the IDP
                // path does not have, so this branch could only ever fail.
                notifications?.onError?.('Profil kunne ikke opdateres');
                throw new Error('Profilopdatering kræver .NET-klienten');
            },
            onSuccess: () => {
                if (currentUser?.uid) {
                    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.USER, currentUser.uid] });
                    // Editing your own profile must also refresh the employee picker /
                    // user list, which reads [CACHE_KEYS.USERS, …]. (#1364)
                    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.USERS] });
                    notifications?.onSuccess?.('Profil opdateret');
                }
            },
            onError: (error) => {
                console.error('Error updating current user:', error);
                notifications?.onError?.('Fejl ved opdatering af profil');
            },
        });
    };

    const useDeleteUser = () => {
        return useMutation({
            mutationFn: async (userId: string) => {
                // API mounted (.NET/IDP path) → deactivate via the admin member PATCH.
                // "Slet bruger" is a soft-deactivate (isActive:false); the list filters
                // `isActive !== false`, so the row drops out. Sending an explicit
                // isActive:false is required — the backend only writes the flag when the
                // param is non-null, so omitting it 200s without deactivating.
                if (api) {
                    const result = await api.users.update(userId, { isActive: false });
                    if (result.ok) {
                        return;
                    }
                    // A real failure (403/500/…) must reach onError, not silently
                    // resolve — otherwise "delete" is a false-success no-op again.
                    throw new Error(
                        result.error?.detail ?? result.error?.title ?? 'Bruger kunne ikke slettes',
                    );
                }

                // No API client mounted. The Cloud Function fallback that used to sit
                // here is gone (#2121): it required a Firebase session, and the app has
                // authenticated against RsConnect since the IDP cutover, so it answered
                // 401 UNAUTHENTICATED for every caller. Fail loudly instead of calling
                // something that cannot succeed -- a silent resolve here would report
                // "Bruger slettet" for a user that is still active.
                throw new Error('Bruger kunne ikke slettes: API-klienten er ikke tilgaengelig.');
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.USERS] });
                if (selectedCompany?.id) {
                    queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.COMPANY, selectedCompany.id] });
                }
                notifications?.onSuccess?.('Bruger slettet');
            },
            // On the mutation rather than inside mutationFn, so it covers BOTH throw
            // sites. Callers that use mutate() (not mutateAsync) never see a rejected
            // promise, so without this a failed delete is silent and the row simply
            // stays put with no explanation.
            onError: (error: unknown) => {
                console.error('Error deleting user:', error);
                notifications?.onError?.(
                    error instanceof Error && error.message ? error.message : 'Kunne ikke slette bruger',
                );
            },
        });
    };

    const useUsersByCompany = (companyId?: string) => {
        const targetCompanyId = companyId || selectedCompany?.id;

        return useQuery({
            queryKey: [CACHE_KEYS.USERS, 'company', targetCompanyId],
            queryFn: async (): Promise<UserModel[]> => {
                if (!api || !targetCompanyId) {
                    return [];
                }

                // Company members come from the .NET `companies.listMembers` endpoint, gated
                // on the role-managed `users.view` permission so the employee pickers work for
                // anyone whose role grants it (not only admins) — and on every auth path,
                // including the IDP path where the legacy Firestore COMPANIES/USERS reads (and
                // their currentUser.uid gating) returned nothing. The members DTO is lean
                // (identity, membership, employment details and the planning team) and
                // intentionally carries no salary or other sensitive employee fields; see
                // mapMemberDto.
                const result = await api.companies.listMembers(targetCompanyId);
                if (!result.ok) {
                    // 403 (caller lacks users.view) yields an empty picker rather than an error;
                    // anything else is surfaced so the query reports a failure.
                    if (result.error.status === 403) {
                        return [];
                    }
                    console.error('Error fetching users by company:', result.error);
                    throw new Error(result.error.title ?? result.error.detail ?? 'Failed to load company members');
                }

                return result.data.map((member) => mapMemberDto(member));
            },
            enabled: !!api && !!targetCompanyId,
        });
    };

    /// Paged, server-searched company members for pickers. Unlike `useUsersByCompany` this never
    /// loads the whole roster: page 1 (A→Z by name) when the picker opens, the next page on
    /// `fetchNextPage()` (infinite scroll), and a fresh page 1 whenever `search` changes. Callers
    /// debounce `search`. Reads `GET .../members/search`, a new endpoint — `useUsersByCompany`'s
    /// unpaged `listMembers` is untouched and stays the source for full-roster reads (name
    /// enrichment, permission filtering, recommendation scoring).
    ///
    /// The key starts with CACHE_KEYS.USERS, so every member mutation's invalidation refreshes it
    /// too (mirrors `useCustomerSearch` in useCustomerOperations.tsx).
    const useMemberSearch = ({ search, enabled, companyId, pageSize = MEMBER_PICKER_PAGE_SIZE, excludeUserIds = [] }: UseMemberSearchOptions) => {
        const targetCompanyId = companyId || selectedCompany?.id;
        const term = search.trim();
        const excluded = [...new Set(excludeUserIds)].sort();
        return useInfiniteQuery({
            queryKey: [CACHE_KEYS.USERS, 'search', targetCompanyId, term, pageSize, excluded],
            queryFn: async ({ pageParam }): Promise<MemberSearchPage> => {
                if (!api || !targetCompanyId) {
                    throw new Error('Ingen API-forbindelse');
                }
                const response = await api.companies.searchMembers(targetCompanyId, {
                    page: pageParam,
                    pageSize,
                    search: term || undefined,
                    excludeUserIds: excluded.length > 0 ? excluded : undefined,
                });
                if (!response.ok) {
                    throw new Error(response.error?.detail ?? response.error?.title ?? 'Fejl ved hentning af medarbejdere');
                }
                return {
                    items: response.data.items.map((member) => mapMemberDto(member)),
                    page: response.data.page,
                    // Trust the server's echoed pageSize: it clamps, and the next-page math must match.
                    pageSize: response.data.pageSize > 0 ? response.data.pageSize : pageSize,
                    total: response.data.total,
                };
            },
            initialPageParam: 1,
            // Keep showing the previous results while the next search term loads, instead of
            // flashing an empty/loading list on every keystroke.
            placeholderData: keepPreviousData,
            getNextPageParam: (last) =>
                last.items.length > 0 && last.page * last.pageSize < last.total ? last.page + 1 : undefined,
            enabled: enabled && !!api && !!targetCompanyId,
        });
    };

    /// One numbered page of company members, for the Users list: search, team filter and sort all
    /// run server-side, so `total` is the true count of matching members rather than of the rows
    /// that happen to be loaded. Keeps the previous page on screen while the next one loads.
    /// Keyed under CACHE_KEYS.USERS like `useMemberSearch`, so member mutations refresh it.
    const useMembersPage = ({ page, pageSize, search, teamIds = [], sort, desc }: UseMembersPageOptions) => {
        const targetCompanyId = selectedCompany?.id;
        const term = search.trim();
        return useQuery({
            queryKey: [CACHE_KEYS.USERS, 'page', targetCompanyId, page, pageSize, term, [...teamIds], sort, desc],
            queryFn: async (): Promise<MemberSearchPage> => {
                if (!api || !targetCompanyId) {
                    throw new Error('Ingen API-forbindelse');
                }
                const response = await api.companies.searchMembers(targetCompanyId, {
                    page,
                    pageSize,
                    search: term || undefined,
                    teamIds: teamIds.length > 0 ? teamIds : undefined,
                    sort,
                    desc: desc || undefined,
                });
                if (!response.ok) {
                    throw new Error(response.error?.detail ?? response.error?.title ?? 'Fejl ved hentning af medarbejdere');
                }
                return {
                    items: response.data.items.map((member) => mapMemberDto(member)),
                    page: response.data.page,
                    pageSize: response.data.pageSize,
                    total: response.data.total,
                };
            },
            placeholderData: keepPreviousData,
            enabled: !!api && !!targetCompanyId,
        });
    };

    /// Specific members by id — a record's preselected people (project leaders), who may sit on
    /// a page the picker never loads. Sent in batches of 200 ids (the server's page cap), so no
    /// id is silently dropped. Ids the server does not return are no longer company members.
    const useMembersByIds = (userIds: readonly string[], enabled = true) => {
        const targetCompanyId = selectedCompany?.id;
        const batches = batchMemberIds(userIds);
        return useQuery({
            queryKey: [CACHE_KEYS.USERS, 'byIds', targetCompanyId, batches.flat()],
            queryFn: async (): Promise<UserModel[]> => {
                if (!api || !targetCompanyId) {
                    throw new Error('Ingen API-forbindelse');
                }
                const pages = await Promise.all(
                    batches.map(async (batch) => {
                        const response = await api.companies.searchMembers(targetCompanyId, {
                            userIds: batch,
                            pageSize: batch.length,
                        });
                        if (!response.ok) {
                            throw new Error(response.error?.detail ?? response.error?.title ?? 'Fejl ved hentning af medarbejdere');
                        }
                        return response.data.items.map((member) => mapMemberDto(member));
                    }),
                );
                return pages.flat();
            },
            enabled: enabled && batches.length > 0 && !!api && !!targetCompanyId,
        });
    };

    return {
        useCreateUserWithAuth,
        useFetchUser,
        useUpdateUser,
        useUpdateCurrentUser,
        useDeleteUser,
        useUsersByCompany,
        useMemberSearch,
        useMembersPage,
        useMembersByIds,
    };
}
